/**
 * EmailRecipientsField — reusable multi-recipient input with autocomplete.
 *
 * Behavior matches Gmail / Outlook:
 *  - Type 2+ chars → suggestions popover (leads / contacts / users from the CRM).
 *  - ↓/↑ navigate, Enter or Tab pick the highlighted entry.
 *  - Click an option (uses `onMouseDown + preventDefault` so the input never blurs mid-click).
 *  - Comma, Enter, or Tab on a typed-but-not-picked value commits it as a chip.
 *  - Backspace on an empty input deletes the last chip.
 *
 * The field operates on a string-array (`value`) so it serializes cleanly to the
 * backend payload (`to: [...emails]`), and surfaces every recipient as a
 * removable chip for clarity.
 */
import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { X } from 'lucide-react';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function extractEmail(s) {
  const m = String(s || '').match(/<([^>]+)>/);
  return (m ? m[1] : s).trim();
}

export default function EmailRecipientsField({
  label,
  value = [],
  onChange,
  placeholder = 'Type a name or email…',
  testIdPrefix = 'recipients',
  autoFocus = false,
}) {
  const [draft, setDraft] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const blurTimerRef = useRef(null);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  const fetchSuggestions = async (q) => {
    if (!q || q.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/email/autocomplete?q=${encodeURIComponent(q)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const items = r.data?.suggestions || [];
      setSuggestions(items);
      setShowSuggestions(items.length > 0);
      setActiveIndex(0);
    } catch (e) {
      // non-fatal — autocomplete is opportunistic
      console.error('autocomplete failed', e);
    }
  };

  const commit = (raw) => {
    const email = extractEmail(raw);
    if (!email) return;
    if (!EMAIL_RE.test(email)) return;
    if (value.includes(email)) {
      setDraft('');
      return;
    }
    onChange([...value, email]);
    setDraft('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const removeChip = (i) => {
    const next = [...value];
    next.splice(i, 1);
    onChange(next);
  };

  const handleKeyDown = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const pick = suggestions[activeIndex];
        if (pick?.email) commit(pick.email);
        return;
      }
      if (e.key === 'Escape') {
        setShowSuggestions(false);
        return;
      }
    }
    if ((e.key === 'Enter' || e.key === ',' || e.key === 'Tab') && draft.trim()) {
      e.preventDefault();
      commit(draft);
      return;
    }
    if (e.key === 'Backspace' && !draft && value.length > 0) {
      removeChip(value.length - 1);
    }
  };

  return (
    <div>
      {label && <label className="block text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">{label}</label>}
      <div
        className="flex flex-wrap items-center gap-1.5 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500 transition-colors"
        onClick={() => inputRef.current?.focus()}
        data-testid={`${testIdPrefix}-container`}
      >
        {value.map((em, i) => (
          <span
            key={`${em}-${i}`}
            className="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-sm px-2 py-0.5 rounded"
            data-testid={`${testIdPrefix}-chip-${i}`}
          >
            {em}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeChip(i); }}
              className="hover:text-red-600 dark:hover:text-red-400"
              data-testid={`${testIdPrefix}-chip-remove-${i}`}
              aria-label={`Remove ${em}`}
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => { setDraft(e.target.value); fetchSuggestions(e.target.value); }}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          onBlur={() => {
            // Delay so a pending mousedown on a suggestion still wins.
            blurTimerRef.current = setTimeout(() => {
              setShowSuggestions(false);
              if (draft.trim()) commit(draft);  // auto-commit pasted email on blur
            }, 200);
          }}
          onKeyDown={handleKeyDown}
          className="flex-1 min-w-[140px] outline-none bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500"
          placeholder={value.length === 0 ? placeholder : ''}
          autoComplete="off"
          role="combobox"
          aria-expanded={showSuggestions && suggestions.length > 0}
          aria-controls={`${testIdPrefix}-listbox`}
          aria-activedescendant={
            showSuggestions && suggestions[activeIndex]
              ? `${testIdPrefix}-opt-${activeIndex}` : undefined
          }
          data-testid={`${testIdPrefix}-input`}
        />
      </div>
      {showSuggestions && suggestions.length > 0 && (
        <div
          id={`${testIdPrefix}-listbox`}
          role="listbox"
          className="relative"
        >
          <div className="absolute z-30 left-0 right-0 mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg max-h-56 overflow-y-auto">
            {suggestions.map((s, idx) => (
              <button
                key={`${s.email || s.id}-${idx}`}
                id={`${testIdPrefix}-opt-${idx}`}
                role="option"
                aria-selected={idx === activeIndex}
                type="button"
                onMouseDown={(e) => {
                  // Beat the input's onBlur so the pick lands.
                  e.preventDefault();
                  if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
                  if (s.email) commit(s.email);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`w-full px-3 py-2 text-left flex items-center gap-2 transition-colors ${
                  idx === activeIndex
                    ? 'bg-blue-50 dark:bg-blue-900/40'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-700/50'
                }`}
                data-testid={`${testIdPrefix}-suggestion-${idx}`}
              >
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide flex-shrink-0 ${
                    s.type === 'lead'
                      ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                      : s.type === 'contact'
                      ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400'
                  }`}
                >
                  {s.type}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                    {s.name || s.email}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                    {s.email}
                    {s.organization && ` · ${s.organization}`}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
